import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import type { OpportunitySendReceipt } from '~/features/shared/createOpportunitiesMachine';
import {
  MarketContentPanel,
  MarketPanelHeader,
  MarketStatusPill,
} from '~/features/shared/market-content';
import { MessageComposer } from '~/features/shared/market-content/MessageComposer';
import { JobMessage, JobOpportunity } from '../types';
import { MessagesSection } from './MessagesSection';

type Props = {
  opportunity: JobOpportunity;
  messages: JobMessage[];
  messagesLoading: boolean;
  sendingMessage: boolean;
  actionsDisabled?: boolean;
  sendReceipt?: OpportunitySendReceipt | null;
  initialDraft?: string;
  onDraftChange?: (messageBody: string) => void;
  /** A refused reply for this response, shown in the composer in place of the privacy note. */
  replyError?: string | null;
  currentProviderId: number | undefined;
  onSendMessage: (messageBody: string) => void;
};

export const ResponseDetail = ({
  opportunity,
  messages,
  messagesLoading,
  sendingMessage,
  actionsDisabled = false,
  sendReceipt,
  initialDraft = '',
  onDraftChange,
  replyError = null,
  currentProviderId,
  onSendMessage,
}: Props) => {
  const [messageText, setMessageText] = useState(initialDraft);
  const submittedText = useRef<string | null>(null);
  const previousReceipt = useRef(sendReceipt);
  const { listingOwner } = opportunity;

  useEffect(() => {
    if (
      sendReceipt !== previousReceipt.current &&
      sendReceipt?.responseId === opportunity.responseId
    ) {
      const acknowledgedText = submittedText.current;
      if (acknowledgedText !== null)
        setMessageText((current) => (current === acknowledgedText ? '' : current));
      submittedText.current = null;
    }
    previousReceipt.current = sendReceipt;
  }, [sendReceipt, opportunity.responseId]);

  const handleSend = () => {
    const trimmed = messageText.trim();
    if (!trimmed || sendingMessage || actionsDisabled) return;
    submittedText.current = messageText;
    onSendMessage(trimmed);
  };

  return (
    <MarketContentPanel
      className="h-full"
      bodyClassName="flex min-h-0 flex-1 flex-col p-0"
      variant="scroll"
    >
      <div className="border-b border-grey-900 p-4 text-left sm:p-5">
        <div className="flex items-center gap-4">
          <div
            className={classNames(
              'flex h-10 w-10 items-center justify-center rounded-md text-sm font-bold tracking-wide text-white',
              listingOwner.masked ? 'bg-grey-500' : 'bg-primary-t-50'
            )}
          >
            {listingOwner.masked ? 'ID' : listingOwner.displayName.charAt(0)}
          </div>
          <div>
            <h3 className="body-text-sb">{listingOwner.displayName}</h3>
            <MarketStatusPill
              status={listingOwner.masked ? 'masked' : 'unmasked'}
              label={listingOwner.masked ? 'Identity masked' : 'Listing owner'}
            />
          </div>
        </div>
      </div>

      <div className="flex flex-1 flex-col overflow-hidden">
        <div className="px-4 pt-4 pb-2">
          <MarketPanelHeader title={`Messages (${messages.length})`} />
        </div>
        <MessagesSection
          messages={messages}
          currentProviderId={currentProviderId}
          loading={messagesLoading}
        />
      </div>

      <MessageComposer
        value={messageText}
        onChange={(text) => {
          setMessageText(text);
          onDraftChange?.(text);
        }}
        onSend={handleSend}
        disabled={sendingMessage || actionsDisabled}
        sending={sendingMessage}
        masked={listingOwner.masked}
        error={replyError}
        label="Message to listing owner"
        sendLabel="Send"
        inputTag="message-input"
        sendTag="btn-send-message"
      />
    </MarketContentPanel>
  );
};
